import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Grid } from "@mui/material";
import { ISearchProductDetails } from "../../shared/models/searched-products-details.interface";
import { getFixcenterSearchedProducts } from "../../services/products.service";
import SecondLevelProductCard from "../../components/second-level-product-card/SecondLevelProductCard";
import { Category } from "../../components/products-page/components/products-categories/models/Category.model";
import { ISearchFilter } from "../../shared/models/searched-products-filters.interface";

interface Props {
    categories: ISearchProductDetails[];
    filter: ISearchFilter;
}

const SearchCategory: React.FC<Props> = ({ categories, filter }) => {
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [data, setData] = useState<ISearchProductDetails[]>([]);

    const loaderRef = useRef<HTMLDivElement | null>(null);

    const fetchProductsFromApi = async (pageNumber: number): Promise<ISearchProductDetails[]> => {
        try {
            filter.page = pageNumber;
            const response = await getFixcenterSearchedProducts(filter);
            const spds: ISearchProductDetails[] = response.data;
            return spds ?? [];
        } catch {
            return [];
        }
    };

    const loadPage = async (pageNumber: number) => {
        if (!hasMore) return;

        setLoading(true);
        const newProducts = await fetchProductsFromApi(pageNumber);

        if (newProducts.length === 0) {
            setHasMore(false);
            setLoading(false);
            return;
        }

        setData(prev => [...prev, ...newProducts]);
        setLoading(false);
    };

    useEffect(() => {
        setData(categories);
        if (categories.length === 0) setHasMore(false);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loading && hasMore) {
                    const nextPage = page + 1;
                    setPage(nextPage);
                    loadPage(nextPage);
                }
            },
            { threshold: 1 }
        );

        if (loaderRef.current) observer.observe(loaderRef.current);

        return () => observer.disconnect();
    }, [loaderRef, loading, page, hasMore]);

    return (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%", mt: 4 }}>
            <Box sx={{ width: "100%", maxWidth: "1400px", px: 2 }}>
                <Grid container spacing={2}>
                    {data.map((item) => (
                        <Grid key={item.categoryUuid} item xs={12} sm={6} md={4} lg={3}>
                            <SecondLevelProductCard
                                categoryName={item.mainCategoryName}
                                product={{
                                    isDirectory: false,
                                    parentProductId: item.parentCategoryUuid,
                                    id: item.categoryUuid,
                                    translation: item.translationCategory,
                                    imageIds: [item.categoryImageId],
                                    routeCatagoryName: item.mainCategoryName
                                } as Category}
                            />
                        </Grid>
                    ))}
                </Grid>

                <Box ref={loaderRef} sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    {loading && <CircularProgress />}
                </Box>
            </Box>
        </Box>
    );
};

export default SearchCategory;